import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiShoppingCart, FiDollarSign, FiUsers, FiTrendingUp,
  FiPackage, FiClock, FiCheckCircle, FiTruck,
  FiArrowRight, FiRefreshCw
} from "react-icons/fi";

/* ═══════════════════════════════════════════
   NEUROMORPHISM STYLES — VIBRANT COLORS
   Blue · Red · Green · Purple · Amber palette
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .puap-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    min-height: 100%;
    padding: 6px 5px;
  }
  .puap-root *, .puap-root *::before, .puap-root *::after {
    box-sizing: border-box;
  }

  /* ── Scrollbar ── */
  .puap-root ::-webkit-scrollbar { width: 6px; }
  .puap-root ::-webkit-scrollbar-track { 
    background: #d1d6df;
    border-radius: 99px;
    box-shadow: inset 0 1px 3px rgba(0,0,0,.1);
  }
  .puap-root ::-webkit-scrollbar-thumb { 
    background: #1e3a5f;
    border-radius: 99px;
    box-shadow: inset 0 1px 2px rgba(255,255,255,.3), inset 0 -1px 2px rgba(0,0,0,.1);
  }

  /* ── Page wrapper ── */
  .puap-page {
    max-width: 1280px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 28px;
  }

  /* ══════════════════════════════
     NEUROMORPHISM BASE SHADOWS
  ══════════════════════════════ */
  .neu-shadow {
    box-shadow: 8px 8px 16px #b8bec8, -8px -8px 16px #ffffff;
  }
  .neu-shadow-inset {
    box-shadow: inset 5px 5px 12px #b8bec8, inset -5px -5px 12px #ffffff;
  }
  .neu-shadow-pressed {
    box-shadow: inset 3px 3px 8px #b8bec8, inset -3px -3px 8px #ffffff;
  }

  /* ══════════════════════════════
     HERO HEADER — BLUE THEME
  ══════════════════════════════ */
  .puap-hero {
    background: linear-gradient(135deg, #0a1628 0%, #0f2040 60%, #1a2a4f 100%);
    border-radius: 9px;
    padding: 32px 32px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    box-shadow: 12px 12px 24px #8f95a0, -12px -12px 24px #ffffff;
  }
  .puap-hero::before {
    content: '';
    position: absolute;
    top: -20px; right: -20px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .puap-hero::after {
    content: '';
    position: absolute;
    bottom: -30px; left: 20%;
    width: 150px; height: 150px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(239,68,68,.1) 0%, transparent 70%);
    pointer-events: none;
  }
  .puap-hero-left { position: relative; z-index: 1; }
  .puap-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(59,130,246,.2);
    border-radius: 99px;
    padding: 6px 16px;
    font-size: 11px;
    font-weight: 700;
    color: #60a5fa;
    letter-spacing: .06em;
    text-transform: uppercase;
    margin-bottom: 14px;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(59,130,246,.3);
  }
  .puap-hero-badge-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #3b82f6;
    animation: pulse-dot-blue 2s infinite;
    box-shadow: 0 0 4px #3b82f6;
  }
  @keyframes pulse-dot-blue {
    0%, 100% { opacity: 1; transform: scale(1); background: #3b82f6; }
    50% { opacity: .5; transform: scale(1.2); background: #60a5fa; }
  }
  .puap-hero-title {
    font-size: clamp(24px, 4vw, 32px);
    font-weight: 800;
    color: #ffffff;
    line-height: 1.2;
    margin-bottom: 8px;
    text-shadow: 2px 2px 4px rgba(0,0,0,.2);
  }
  .puap-hero-sub {
    font-size: 14px;
    font-weight: 500;
    color: #94a3b8;
  }
  .puap-hero-id {
    font-size: 12px;
    font-weight: 600;
    color: #fbbf24;
    margin-top: 8px;
  }
  .puap-hero-stat {
    background: rgba(255,255,255,.1);
    border-radius: 9px;
    padding: 20px 28px;
    text-align: center;
    min-width: 150px;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255,255,255,.15);
    box-shadow: 8px 8px 16px rgba(0,0,0,.2), -4px -4px 12px rgba(255,255,255,.1);
  }
  .puap-hero-stat-val {
    font-size: 28px;
    font-weight: 800;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .puap-hero-stat-label {
    font-size: 11px;
    font-weight: 600;
    color: #cbd5e1;
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  /* ══════════════════════════════
     METRICS GRID — COLORFUL CARDS
  ══════════════════════════════ */
  .puap-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  @media (max-width: 900px) { .puap-metrics { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .puap-metrics { grid-template-columns: 1fr; } }

  .puap-metric-card {
    border-radius: 9px;
    padding: 22px;
    transition: all 0.2s ease;
    position: relative;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .puap-metric-card:hover {
    transform: translateY(-3px);
    box-shadow: 12px 12px 24px #8f95a0, -12px -12px 24px #ffffff;
  }
  
  /* Colorful card backgrounds */
  .puap-metric-card.blue { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
  .puap-metric-card.green { background: linear-gradient(135deg, #064e3b 0%, #10b981 100%); }
  .puap-metric-card.purple { background: linear-gradient(135deg, #4c1d95 0%, #8b5cf6 100%); }
  .puap-metric-card.amber { background: linear-gradient(135deg, #78350f 0%, #f59e0b 100%); }
  .puap-metric-card.red { background: linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%); }

  .puap-metric-card.blue::after,
  .puap-metric-card.green::after,
  .puap-metric-card.purple::after,
  .puap-metric-card.amber::after,
  .puap-metric-card.red::after {
    content: '';
    position: absolute;
    bottom: 0; left: 15%; right: 15%;
    height: 4px;
    border-radius: 4px 4px 0 0;
    background: rgba(255,255,255,.5);
    box-shadow: 0 -2px 8px rgba(255,255,255,.3);
  }

  .puap-metric-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .puap-metric-label {
    font-size: 11px;
    font-weight: 700;
    color: rgba(255,255,255,.8);
    text-transform: uppercase;
    letter-spacing: .08em;
  }
  .puap-metric-icon {
    width: 44px; height: 44px;
    border-radius: 20px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    background: rgba(255,255,255,.2);
    backdrop-filter: blur(4px);
  }
  .puap-metric-icon.blue,
  .puap-metric-icon.green,
  .puap-metric-icon.purple,
  .puap-metric-icon.amber,
  .puap-metric-icon.red {
    color: white;
  }

  .puap-metric-value {
    font-size: clamp(22px, 3vw, 28px);
    font-weight: 800;
    color: #ffffff;
  }
  .puap-metric-unit {
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,.7);
    margin-top: 4px;
  }

  /* ══════════════════════════════
     TWO-COL GRID
  ══════════════════════════════ */
  .puap-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
  @media (max-width: 768px) { .puap-two-col { grid-template-columns: 1fr; } }

  /* ══════════════════════════════
     CARDS
  ══════════════════════════════ */
  .puap-card {
    background: #e0e5ec;
    border-radius: 9px;
    padding: 26px;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .puap-card-title {
    font-size: 13px;
    font-weight: 800;
    color: #0f172a;
    text-transform: uppercase;
    letter-spacing: .07em;
    margin-bottom: 22px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .puap-card-title-bar {
    width: 5px; height: 20px;
    border-radius: 99px;
    background: linear-gradient(180deg, #f59e0b, #ef4444);
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0,0,0,.1);
  }

  /* ── Commission rows ── */
  .puap-commission-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-radius: 9px;
    background: #e0e5ec;
    margin-bottom: 12px;
    transition: all 0.2s;
    box-shadow: inset 2px 2px 5px #b8bec8, inset -2px -2px 5px #ffffff;
  }
  .puap-commission-row:hover {
    box-shadow: inset 3px 3px 6px #b8bec8, inset -3px -3px 6px #ffffff;
  }
  .puap-commission-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
  }
  .puap-commission-pct {
    font-size: 11px;
    font-weight: 700;
    margin-top: 4px;
    padding: 3px 10px;
    border-radius: 99px;
    display: inline-block;
  }
  .puap-commission-pct.blue { background: #2563eb; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-commission-pct.green { background: #059669; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-commission-pct.orange { background: #ea580c; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-commission-pct.red { background: #dc2626; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-commission-amount {
    font-size: 16px;
    font-weight: 800;
  }
  .puap-commission-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-radius: 24px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    margin-top: 8px;
    box-shadow: 5px 5px 12px #8f95a0, -5px -5px 12px #ffffff;
  }
  .puap-commission-total-label {
    font-size: 13px;
    font-weight: 700;
    color: rgba(255,255,255,.9);
  }
  .puap-commission-total-val {
    font-size: 22px;
    font-weight: 900;
    color: #fbbf24;
  }

  /* ── Delivery status ── */
  .puap-delivery-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 9px;
    background: #e0e5ec;
    margin-bottom: 12px;
    transition: all 0.2s;
    box-shadow: inset 2px 2px 5px #b8bec8, inset -2px -2px 5px #ffffff;
  }
  .puap-delivery-row:hover {
    box-shadow: inset 3px 3px 6px #b8bec8, inset -3px -3px 6px #ffffff;
  }
  .puap-delivery-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .puap-delivery-icon {
    width: 38px; height: 38px;
    border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    background: rgba(0,0,0,.05);
    box-shadow: inset 1px 1px 2px #ffffff, inset -1px -1px 2px #b8bec8;
  }
  .puap-delivery-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
  }
  .puap-delivery-badge {
    font-size: 12px;
    font-weight: 700;
    padding: 5px 14px;
    border-radius: 99px;
  }
  .puap-delivery-badge.yellow { background: #d97706; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-delivery-badge.blue { background: #2563eb; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-delivery-badge.green { background: #059669; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }
  .puap-delivery-badge.red { background: #dc2626; color: white; box-shadow: inset 1px 1px 2px rgba(255,255,255,.2), inset -1px -1px 2px rgba(0,0,0,.1); }

  /* ══════════════════════════════
     WALLET STRIP — COLORFUL
  ══════════════════════════════ */
  .puap-wallet-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  @media (max-width: 640px) { .puap-wallet-grid { grid-template-columns: 1fr; } }

  .puap-wallet-card {
    border-radius: 9px;
    padding: 20px;
    transition: all 0.2s;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .puap-wallet-card:hover {
    transform: translateY(-2px);
  }
  .puap-wallet-card.green { background: linear-gradient(135deg, #064e3b, #10b981); }
  .puap-wallet-card.purple { background: linear-gradient(135deg, #4c1d95, #8b5cf6); }
  .puap-wallet-card.blue { background: linear-gradient(135deg, #1e3a5f, #2563eb); }
  .puap-wallet-card.red { background: linear-gradient(135deg, #7f1d1d, #ef4444); }

  .puap-wallet-emoji { font-size: 32px; margin-bottom: 12px; }
  .puap-wallet-label {
    font-size: 10px;
    font-weight: 800;
    color: rgba(255,255,255,.8);
    text-transform: uppercase;
    letter-spacing: .08em;
    margin-bottom: 8px;
  }
  .puap-wallet-amount {
    font-size: clamp(20px, 2.5vw, 24px);
    font-weight: 900;
    color: #ffffff;
  }
  .puap-wallet-pending {
    font-size: 11px;
    font-weight: 600;
    color: #fcd34d;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* ══════════════════════════════
     TEAM STATS — COLORFUL
  ══════════════════════════════ */
  .puap-team-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  @media (max-width: 600px) { .puap-team-grid { grid-template-columns: 1fr; } }

  .puap-team-card {
    border-radius: 9px;
    padding: 24px 16px;
    text-align: center;
    transition: all 0.2s ease;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
    background: #e0e5ec;
  }
  .puap-team-card:hover {
    transform: translateY(-3px);
  }
  .puap-team-card:nth-child(1) { background: linear-gradient(135deg, #1e3a5f, #3b82f6); }
  .puap-team-card:nth-child(2) { background: linear-gradient(135deg, #064e3b, #10b981); }
  .puap-team-card:nth-child(3) { background: linear-gradient(135deg, #78350f, #f59e0b); }
  
  .puap-team-emoji { font-size: 36px; margin-bottom: 14px; }
  .puap-team-val {
    font-size: clamp(24px, 3vw, 30px);
    font-weight: 900;
    color: #ffffff;
    margin-bottom: 6px;
  }
  .puap-team-label {
    font-size: 11px;
    font-weight: 700;
    color: rgba(255,255,255,.8);
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  /* ══════════════════════════════
     QUICK ACTIONS
  ══════════════════════════════ */
  .puap-actions-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  @media (max-width: 600px) { .puap-actions-grid { grid-template-columns: 1fr; } }

  .puap-action-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 22px;
    border-radius: 24px;
    background: #e0e5ec;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .puap-action-btn:hover {
    box-shadow: inset 4px 4px 10px #8f95a0, inset -4px -4px 10px #ffffff;
    transform: translateY(2px);
  }
  .puap-action-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .puap-action-icon {
    width: 48px; height: 48px;
    border-radius: 20px;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
  }
  .puap-action-btn:nth-child(1) .puap-action-icon { background: linear-gradient(135deg, #1e3a5f, #3b82f6); color: white; }
  .puap-action-btn:nth-child(2) .puap-action-icon { background: linear-gradient(135deg, #064e3b, #10b981); color: white; }
  .puap-action-btn:nth-child(3) .puap-action-icon { background: linear-gradient(135deg, #4c1d95, #8b5cf6); color: white; }
  .puap-action-label {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
  }
  .puap-action-arrow { 
    color: #64748b; 
    transition: all 0.2s ease;
  }
  .puap-action-btn:hover .puap-action-arrow { 
    color: #f59e0b; 
    transform: translateX(4px);
  }

  /* ══════════════════════════════
     MEMBERSHIP BADGE
  ══════════════════════════════ */
  .mem-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 14px; border-radius: 99px;
    font-size: 11px; font-weight: 700;
    letter-spacing: .05em; text-transform: uppercase;
  }
  .mem-badge.blue {
    background: rgba(59,130,246,.2);
    border: 1px solid rgba(59,130,246,.4);
    color: #60a5fa;
    backdrop-filter: blur(4px);
  }
  .mem-badge-dot {
    width: 7px; height: 7px; border-radius: 50%; 
    background: #3b82f6;
    animation: mem-pulse 2s infinite;
  }
  @keyframes mem-pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 #3b82f6; }
    50% { opacity: .6; box-shadow: 0 0 0 3px rgba(59,130,246,.3); }
  }
  .mem-badge.blue.inactive {
    background: rgba(100,116,139,.2);
    border-color: rgba(100,116,139,.3);
    color: #94a3b8;
  }
  .mem-badge-dot.inactive {
    background: #94a3b8;
    animation: none;
  }
  .flex { display: flex; }
  .items-center { align-items: center; }
  .gap-3 { gap: 12px; }
  .flex-wrap { flex-wrap: wrap; }

  /* ══════════════════════════════
     LOADING / EMPTY STATES
  ══════════════════════════════ */
  .puap-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    gap: 20px;
  }
  .puap-spinner {
    width: 52px; height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a5f, #3b82f6);
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
    animation: puap-spin 1s linear infinite;
    position: relative;
  }
  .puap-spinner::after {
    content: '';
    position: absolute;
    top: 8px; left: 8px; right: 8px; bottom: 8px;
    border-radius: 50%;
    background: #e0e5ec;
  }
  @keyframes puap-spin { 
    from { transform: rotate(0deg); } 
    to { transform: rotate(360deg); } 
  }
  .puap-spinner-text {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
  }

  .puap-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 56px 24px;
    text-align: center;
    background: #e0e5ec;
    border-radius: 28px;
    box-shadow: inset 4px 4px 10px #8f95a0, inset -4px -4px 10px #ffffff;
  }
  .puap-empty-icon { font-size: 48px; margin-bottom: 16px; opacity: 0.7; }
  .puap-empty-text {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
  }

  /* ══════════════════════════════
     SKELETON LOADING
  ══════════════════════════════ */
  .puap-skeleton {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .puap-skel-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  @media (max-width: 900px) { .puap-skel-row { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .puap-skel-row { grid-template-columns: 1fr; } }
  .puap-skel-box {
    border-radius: 28px;
    overflow: hidden;
    background: #e0e5ec;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .puap-skel-inner { 
    height: 120px; 
    background: linear-gradient(90deg, #cbd5e1 25%, #e2e8f0 50%, #cbd5e1 75%);
    background-size: 200% 100%;
    animation: shimmer 1.6s infinite;
  }
  @keyframes shimmer { 
    from { background-position: -200% 0; } 
    to { background-position: 200% 0; } 
  }
  .puap-skel-wide {
    height: 180px;
    border-radius: 28px;
    background: #e0e5ec;
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .puap-skel-wide::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 30%, rgba(255,255,255,.4) 50%, transparent 70%);
    background-size: 200% 100%;
    animation: shimmer 1.6s infinite;
  }
`;

/* ═══════════════════════════════════════════
   MAIN COMPONENT - NO FUNCTIONALITY CHANGES
═══════════════════════════════════════════ */
const PUAPDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data: res } = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/puap-partner`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res);
      } catch (err) {
        console.error("Failed to fetch PU&AP dashboard data:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="puap-root">
      <style>{styles}</style>
      <div className="puap-skeleton">
        <div className="puap-skel-wide" />
        <div className="puap-skel-row">
          {[1, 2, 3, 4].map(i => (
            <div className="puap-skel-box" key={i}><div className="puap-skel-inner" /></div>
          ))}
        </div>
        <div className="puap-skel-wide" />
      </div>
    </div>
  );

  if (!data) return (
    <div className="puap-root">
      <style>{styles}</style>
      <div className="puap-empty">
        <div className="puap-empty-icon">😕</div>
        <div className="puap-empty-text">Unable to load PU&AP dashboard</div>
      </div>
    </div>
  );

  const activationRate = data.team.directReferrals > 0
    ? Math.round((data.team.activeMembers / data.team.directReferrals) * 100)
    : 0;

  return (
    <div className="puap-root">
      <style>{styles}</style>
      <div className="puap-page">

        {/* ── Hero ── */}
        <div className="puap-hero">
          <div className="puap-hero-left">
            <div className="puap-hero-badge">
              <div className="puap-hero-badge-dot" />
              PU&AP Partner
            </div>
            <div className="puap-hero-title">Product User and Advisor Partnership Dashboard</div>
            {/* <div className="puap-hero-sub">Product User and Advisor Partnership</div> */}
            <div className="puap-hero-id flex items-center gap-3 flex-wrap">
              Unique ID: {data.user.uniqueId}
              {data?.user?.hasProductPurchase ? (
                <span className="mem-badge blue">
                  <span className="mem-badge-dot" /> PU&AP Active
                </span>
              ) : (
                <span className="mem-badge blue inactive">
                  <span className="mem-badge-dot inactive" /> PU&AP Inactive
                </span>
              )}
            </div>

          </div>
          <div className="puap-hero-stat">
            <div className="puap-hero-stat-val">
              ₹{data.commissions.totalCommission.toLocaleString("en-IN")}
            </div>
            <div className="puap-hero-stat-label">Total Earned</div>
          </div>
        </div>

        {/* ── Metrics ── */}
        <div className="puap-metrics">
          <MetricCard
            color="blue" title="Total Purchases"
            value={data.purchases.totalPurchases} unit="orders"
            icon={<FiShoppingCart size={18} />}
          />
          <MetricCard
            color="green" title="Total Spent"
            value={`₹${data.purchases.totalSpent.toLocaleString("en-IN")}`}
            icon={<FiDollarSign size={18} />}
          />
          <MetricCard
            color="purple" title="Commissions Earned"
            value={`₹${data.commissions.totalCommission.toLocaleString("en-IN")}`}
            icon={<FiTrendingUp size={18} />}
          />
          <MetricCard
            color="amber" title="Direct Team"
            value={data.team.directReferrals} unit="members"
            icon={<FiUsers size={18} />}
          />
        </div>

        {/* ── Commission + Delivery ── */}
        <div className="puap-two-col">

          {/* Commission Structure */}
          <div className="puap-card">
            <div className="puap-card-title">
              <div className="puap-card-title-bar" />
              Commission Structure
            </div>

            <CommissionRow
              label="Direct Commission" pctColor="blue"
              amount={data.commissions.directCommission} pct="10%" amtColor="#3b82f6"
            />
            <CommissionRow
              label="Matrix Commission" pctColor="green"
              amount={data.commissions.matrixCommission} pct="5%" amtColor="#10b981"
            />
            <CommissionRow
              label="Generation Commission" pctColor="orange"
              amount={data.commissions.generationCommission} pct="2%" amtColor="#f59e0b"
            />

            <div className="puap-commission-total">
              <span className="puap-commission-total-label">Total Commission</span>
              <span className="puap-commission-total-val">
                ₹{data.commissions.totalCommission.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          {/* Delivery Status */}
          <div className="puap-card">
            <div className="puap-card-title">
              <div className="puap-card-title-bar" />
              Delivery Status
            </div>

            <DeliveryRow
              icon={<FiClock size={15} />} iconBg="#fef3c7" iconColor="#d97706"
              label="Pending Delivery"
              count={data.purchases.deliveryStatus.pending} badgeClass="yellow"
            />
            <DeliveryRow
              icon={<FiTruck size={15} />} iconBg="#dbeafe" iconColor="#2563eb"
              label="Shipped"
              count={data.purchases.deliveryStatus.shipped} badgeClass="blue"
            />
            <DeliveryRow
              icon={<FiCheckCircle size={15} />} iconBg="#d1fae5" iconColor="#059669"
              label="Delivered"
              count={data.purchases.deliveryStatus.delivered} badgeClass="green"
            />

            <div className="puap-commission-total" style={{ marginTop: 8 }}>
              <span className="puap-commission-total-label">Total Orders</span>
              <span className="puap-commission-total-val">{data.purchases.totalPurchases}</span>
            </div>
          </div>
        </div>

        {/* ── Wallet ── */}
        <div className="puap-card">
          <div className="puap-card-title">
            <div className="puap-card-title-bar" />
            Wallet Information
          </div>
          <div className="puap-wallet-grid">
            <WalletCard
              variant="green" emoji="🛍️" label="PU&AP Wallet"
              amount={data.wallet.puapWallet} pending={data.wallet.pendingWithdrawal}
            />
            <WalletCard
              variant="purple" emoji="💰" label="E-Wallet"
              amount={data.wallet.eWallet}
            />
            <WalletCard
              variant="blue" emoji="💳" label="Total Withdrawn"
              amount={data.wallet.totalWithdrawn}
            />
          </div>
        </div>

        {/* ── Activity & Bonuses ── */}
        <div className="puap-card">
          <div className="puap-card-title">
            <div className="puap-card-title-bar" />
            Activity & Bonus Status
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ padding: 14, borderRadius: 10, background: '#eef2ff', color: '#1e3a5f', fontWeight: 700 }}>
              Activity Status: {data.activity.activityStatus || 'inactive'}
            </div>
            <div style={{ padding: 14, borderRadius: 10, background: '#ecfeff', color: '#0f766e', fontWeight: 600 }}>
              PV Purchased: {data.activity.totalPvPurchased.toLocaleString('en-IN')}
            </div>
            <div style={{ padding: 14, borderRadius: 10, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
              Monthly Active Months: {data.activity.monthlyActiveMonths}
            </div>
            <div style={{ padding: 14, borderRadius: 10, background: '#f5f3ff', color: '#6d28d9', fontWeight: 600 }}>
              Bonus Eligibility: {data.bonuses.rewardRank || data.bonuses.carRank || data.bonuses.royaltyRank ? 'Eligible for rank bonus' : 'Standard activity'}
            </div>
          </div>
        </div>

        {/* ── Team ── */}
        <div className="puap-card">
          <div className="puap-card-title">
            <div className="puap-card-title-bar" />
            Team Performance
          </div>
          <div className="puap-team-grid">
            <TeamCard emoji="👥" value={data.team.directReferrals} label="Total Referrals" />
            <TeamCard emoji="✅" value={data.team.activeMembers} label="Active Members" />
            <TeamCard emoji="📊" value={`${activationRate}%`} label="Activation Rate" />
          </div>
        </div>

        {/* ── Recent Activity ── */}
        <div className="puap-card">
          <div className="puap-card-title">
            <div className="puap-card-title-bar" />
            Recent Activity
          </div>
          {data.recentTransactions?.length > 0 ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {data.recentTransactions.slice(0, 6).map((item, index) => (
                <div key={item._id || index} style={{ padding: 12, borderRadius: 10, background: '#e0e5ec', boxShadow: 'inset 2px 2px 5px #b8bec8, inset -2px -2px 5px #ffffff' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{String(item.type).replace(/_/g, ' ')}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>{item.description || 'Transaction recorded'}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#2563eb', marginTop: 6 }}>₹{Number(item.amount || 0).toLocaleString('en-IN')}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="puap-empty">
              <div className="puap-empty-icon">🧾</div>
              <div className="puap-empty-text">No recent activity yet</div>
            </div>
          )}
        </div>

        {/* ── Quick Actions ── */}
        {/* <div className="puap-card">
          <div className="puap-card-title">
            <div className="puap-card-title-bar" />
            Quick Actions
          </div>
          <div className="puap-actions-grid">
            <ActionBtn href="/buy-product" emoji="🛒" label="Buy Products" />
            <ActionBtn href="/wallet" emoji="💳" label="Manage Wallet" />
            <ActionBtn href="/team" emoji="👥" label="View Team" />
          </div>
        </div> */}

        {/* ── Recent Orders ── */}
        {/* <div className="puap-card">
          <div className="puap-card-title">
            <div className="puap-card-title-bar" />
            Recent Orders
          </div>
          <div className="puap-empty">
            <div className="puap-empty-icon">🛒</div>
            <div className="puap-empty-text">No orders yet. Start buying products to earn commissions!</div>
          </div>
        </div> */}

      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SUB-COMPONENTS - NO FUNCTIONALITY CHANGES
═══════════════════════════════════════════ */
const MetricCard = ({ color, title, value, unit, icon }) => (
  <div className={`puap-metric-card ${color}`}>
    <div className="puap-metric-top">
      <div className="puap-metric-label">{title}</div>
      <div className={`puap-metric-icon ${color}`}>{icon}</div>
    </div>
    <div>
      <div className="puap-metric-value">{value}</div>
      {unit && <div className="puap-metric-unit">{unit}</div>}
    </div>
  </div>
);

const CommissionRow = ({ label, pct, pctColor, amount, amtColor }) => (
  <div className="puap-commission-row">
    <div>
      <div className="puap-commission-label">{label}</div>
      <span className={`puap-commission-pct ${pctColor}`}>{pct}</span>
    </div>
    <div className="puap-commission-amount" style={{ color: amtColor }}>
      ₹{amount.toLocaleString("en-IN")}
    </div>
  </div>
);

const DeliveryRow = ({ icon, iconBg, iconColor, label, count, badgeClass }) => (
  <div className="puap-delivery-row">
    <div className="puap-delivery-left">
      <div className="puap-delivery-icon" style={{ background: iconBg, color: iconColor }}>
        {icon}
      </div>
      <span className="puap-delivery-label">{label}</span>
    </div>
    <span className={`puap-delivery-badge ${badgeClass}`}>{count}</span>
  </div>
);

const WalletCard = ({ variant, emoji, label, amount, pending }) => (
  <div className={`puap-wallet-card ${variant}`}>
    <div className="puap-wallet-emoji">{emoji}</div>
    <div className="puap-wallet-label">{label}</div>
    <div className="puap-wallet-amount">₹{amount.toLocaleString("en-IN")}</div>
    {pending > 0 && (
      <div className="puap-wallet-pending">
        <FiClock size={11} /> Pending: ₹{pending.toLocaleString("en-IN")}
      </div>
    )}
  </div>
);

const TeamCard = ({ emoji, value, label }) => (
  <div className="puap-team-card">
    <div className="puap-team-emoji">{emoji}</div>
    <div className="puap-team-val">{value}</div>
    <div className="puap-team-label">{label}</div>
  </div>
);

const ActionBtn = ({ href, emoji, label }) => (
  <a href={href} className="puap-action-btn">
    <div className="puap-action-left">
      <div className="puap-action-icon">{emoji}</div>
      <span className="puap-action-label">{label}</span>
    </div>
    <FiArrowRight size={16} className="puap-action-arrow" />
  </a>
);

export default PUAPDashboard;